import { css } from '@emotion/react'
import {
  Stack,
  StackProps,
  Tooltip,
  tooltipClasses,
  TooltipProps,
  Typography
} from '@mui/material'
import { useEffect, useRef, useState } from 'react'
import { Button, ButtonProps, ButtonVariant } from 'components/common/Button'
import {
  NewFlexContainer,
  NewFlexContainerProps
} from 'components/common/common.style'
import { unifiedColors } from 'styles/colors'
import { styled } from 'styles/styled'
import { themeUtils } from 'styles/theme'

interface SearchElementProps extends StackProps {
  $open?: boolean
  $selected?: boolean
}

type SearchBarHeaderProps = SearchElementProps & {
  $floating?: boolean
  $expanded?: boolean
}

type SearchBarContainerProps = StackProps & {
  $forcedExpanded?: boolean
  $hasTop?: boolean
}

export const SearchBarContainer = styled(Stack)<SearchBarContainerProps>`
  position: relative;
  ${({ $forcedExpanded, $hasTop }) =>
    !$forcedExpanded &&
    css`
      position: absolute;
      top: 0;
      transform: translateX(-50%);
      left: 50%;
      z-index: 101;
      ${$hasTop &&
      css`
        top: 6px;
      `}
    `}
  ${themeUtils.maxWidth.md} {
    width: 100%;
  }
`

export const SearchBarHeader = styled((props: SearchBarHeaderProps) => (
  <Stack
    width="805px"
    justifyContent="space-between"
    alignItems="center"
    padding="5px"
    gap="8px"
    {...props}
  />
))`
  display: grid;
  grid-template-columns: 260px 1px 180px 1px 200px auto;
  border-radius: 16px;
  background-color: ${unifiedColors.button.tertiary.default};
  path {
    stroke: ${unifiedColors.icon.purple};
  }
  p {
    color: ${unifiedColors.icon.purple};
  }
  ${({ $floating, $open }) =>
    $floating &&
    !$open &&
    css`
      width: 100%;
    `}

  ${themeUtils.maxWidth.md} {
    display: flex;
    width: 100%;
    flex-direction: column;
    gap: 0;
    align-items: flex-start;
    border-radius: 24px;
  }
  ${({ $floating, $expanded }) =>
    $floating &&
    !$expanded &&
    css`
      display: flex;
    `}
  ${({ $floating, $expanded }) =>
    $expanded &&
    $floating &&
    css`
      ${themeUtils.minWidth.md} {
        box-shadow: 1px 2px 0px 0px ${unifiedColors.shadow.tertiary};
      }
    `}
`

export const SearchElement = styled((props: SearchElementProps) => (
  <Stack
    gap="6px"
    padding="15px 15px 15px 10px"
    {...props}
    flexDirection="row"
  />
))`
  width: 100%;
  margin: -1px;
  border: 1px solid transparent;
  border-radius: 12px;
  cursor: pointer;
  &:hover {
    background-color: ${unifiedColors.surface.default50};
    ${({ $open }) =>
      $open &&
      css`
        border: 1px solid ${unifiedColors.border.focused.default};
      `}
  }
  ${({ $selected }) =>
    $selected &&
    css`
      background-color: ${unifiedColors.surface.default50};
    `}
  ${themeUtils.maxWidth.md} {
    width: 100%;
    border-radius: 0;
    background-color: transparent;
    &:hover {
      background-color: transparent;
      ${({ $open }) =>
        $open &&
        css`
          border: 1px solid transparent;
        `}
    }
  }
`

export const Separator = styled((props: StackProps) => (
  <Stack height={32} {...props} />
))`
  border: 1px solid ${unifiedColors.button.tertiary.hovered};
  ${themeUtils.maxWidth.md} {
    height: 0;
    width: 95%;
    align-self: center;
  }
`

export const ButtonContainer = styled(NewFlexContainer, {
  shouldForwardProp: (prop) => prop !== 'flexDirection'
})`
  ${themeUtils.maxWidth.md} {
    margin-top: 4px;
    width: 100%;
  }
`

export const SearchButton = styled((props: ButtonProps) => (
  <Button variant={ButtonVariant.Primary} fullWidthOnMobile {...props} />
))`
  ${themeUtils.maxWidth.md} {
    border-radius: 16px;
  }
`

export const MiniSearchBar = styled(SearchElement)`
  width: 100%;
  padding: 0;
  border-radius: 12px;
`

export const MiniButton = styled(Button)`
  border-radius: 12px;
  padding: 10px;
  height: auto;
`

interface SearchBarContentProps extends NewFlexContainerProps {
  $forcedExpanded?: boolean
}

export const SearchBarContent = styled(NewFlexContainer)<SearchBarContentProps>`
  margin-top: 6px;
  max-width: 805px;
  border-radius: 24px;
  background-color: ${unifiedColors.surface.default};
  border: 1px solid ${unifiedColors.shadow.tertiary};
  box-shadow: 2px 3px 0px 0px ${unifiedColors.shadow.tertiary};
  z-index: 101;
  ${({ $forcedExpanded }) =>
    $forcedExpanded &&
    css`
      position: absolute;
      top: 66px;
      transform: translateX(-50%);
      left: 50%;
      border: 1px solid ${unifiedColors.border.default};
      box-shadow: 4px 6px 0px 0px ${unifiedColors.shadow.quaternary};
    `}
`

export const MobileSearchBarHeader = styled(Stack)`
  width: 100%;
  background-color: ${unifiedColors.button.tertiary.default};
  border-radius: 16px;
  box-shadow: 1px 2px 0px 0px ${unifiedColors.shadow.tertiary};

  path {
    stroke: ${unifiedColors.icon.purple};
  }
  p {
    color: ${unifiedColors.icon.purple};
  }
`

type MobileContentProps = StackProps & {
  $open?: boolean
}

export const MobileContent = styled(Stack)<MobileContentProps>``

export const MobileContentHeader = styled((props: StackProps) => (
  <Stack
    flexDirection="row"
    justifyContent="space-between"
    {...props}
    width="100%"
  />
))`
  padding: 20px 16px;
`

interface StyledHeaderLabelProps {
  label: string
}

export const HtmlTooltip = styled(({ className, ...props }: TooltipProps) => (
  <Tooltip {...props} classes={{ popper: className }} />
))`
  & .${tooltipClasses.tooltip} {
    background-color: ${unifiedColors.surface.purple50};
    border-radius: 5px;
  }
`

export const StyledHeaderLabel = ({ label }: StyledHeaderLabelProps) => {
  const textRef = useRef<HTMLDivElement>(null)
  const [isOverflowed, setIsOverflowed] = useState(false)

  useEffect(() => {
    const el = textRef.current
    if (el) {
      const hasOverflow = el.scrollWidth > el.clientWidth
      setIsOverflowed(hasOverflow)
    }
  }, [label])
  return (
    <HtmlTooltip
      title={
        <Typography
          color={unifiedColors.label.onPurple}
          fontSize={12}
          lineHeight={1.4}
          fontWeight={500}
        >
          {label}
        </Typography>
      }
      disableHoverListener={!isOverflowed}
    >
      <Typography noWrap variant="body2" ref={textRef}>
        {label}
      </Typography>
    </HtmlTooltip>
  )
}
